// BrimLight/client/ui/MediaManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class MediaManager extends ComponentBase {
    static styles = `
        .media-manager-modal .modal-dialog {
            max-width: 95vw;
            width: 1200px;
            margin: 1.75rem auto;
        }
        .media-manager-modal .modal-content {
            height: 90vh;
            display: flex;
            flex-direction: column;
        }
        .media-manager-modal .modal-body {
            flex-grow: 1;
            overflow: hidden;
            padding: 0;
            display: flex;
            flex-direction: column;
        }
        .media-toolbar {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            flex-shrink: 0;
        }
        .media-breadcrumbs .breadcrumb-item a {
            cursor: pointer;
        }
        .media-grid-container {
            flex-grow: 1;
            overflow-y: auto;
            padding: 1rem;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 1rem;
        }
        .media-item {
            position: relative;
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
            cursor: pointer;
            aspect-ratio: 1 / 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            overflow: hidden;
        }
        .media-item:hover {
            background-color: var(--bs-tertiary-bg);
        }
        .media-item.selected {
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 2px var(--bs-primary);
        }
        .media-item .item-icon {
            font-size: var(--ab-text-lg);
            color: var(--bs-secondary-color);
        }
        .media-item img, .media-item video {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .media-item .item-name {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0,0,0,0.5);
            color: white;
            font-size: var(--ab-text-xs);
            padding: 0.2rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .selection-checkbox {
            position: absolute;
            top: 5px;
            left: 5px;
            width: 20px;
            height: 20px;
        }
        .media-drop-zone {
            border: 2px dashed var(--bs-primary);
            background-color: var(--bs-primary-bg-subtle);
        }

        /* Mobile-first full-screen modal */
        @media (max-width: 767.98px) {
            .media-manager-modal .modal-dialog {
                margin: 0;
                max-width: 100%;
                height: 100%;
            }
            .media-manager-modal .modal-content {
                height: 100%;
                border-radius: 0;
                border: none;
            }
            .media-grid-container {
                grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                gap: 0.5rem;
                padding: 0.5rem;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.scope = 'subscription'; // 'subscription' or 'global'
        this.currentPath = '';
        this.files = [];
        this.selectedFiles = new Map();
        this.options = {};
        this.modalInstance = null;
        this.fileUpload = null;

        this._resolvePromise = null;
        this._rejectPromise = null;

        this._boundHandleDrop = this._handleDrop.bind(this);
        this._boundHandleDragOver = (e) => e.preventDefault();

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade media-manager-modal';
        this.container.id = `media-manager-${this.config.instanceId}`;
        this.container.setAttribute('tabindex', '-1');
        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <ul class="nav nav-pills">
                            <li class="nav-item"><a class="nav-link active" href="#" data-scope="subscription">My Media</a></li>
                            <li class="nav-item"><a class="nav-link" href="#" data-scope="global">Media Library</a></li>
                        </ul>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="media-toolbar"></div>
                        <div class="media-grid-container"></div>
                    </div>
                    <div class="modal-footer">
                        <span class="me-auto selection-info"></span>
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary confirm-selection-btn" disabled>Select</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.container);
        this.modalInstance = new bootstrap.Modal(this.container);

        this.fileUpload = await this.createManagedComponent('FileUpload', null, {
            instanceId: `${this.config.instanceId}-uploader`,
            hidden: true,
            getUploadKey: () => this.appClient.rpcClient.call('MediaManagerRpc.getUploadKey', {
                scope: this.scope,
                currentPath: this.currentPath,
                fileConfig: { maxFiles: 10, maxFileSize: 100 * 1024 * 1024, fileTypes: ['image/*', 'video/*', 'audio/*'] }
            })
        });

        this.fileUpload.on('uploadCompleted', async () => {
            const uploadKey = this.fileUpload.getValue();
            if (uploadKey) {
                try {
                    await this.appClient.rpcClient.call('MediaManagerRpc.finalizeUpload', { uploadKey });
                    this.appClient.notifier.success("Upload complete!");
                    await this._loadFiles(); // Refresh the file list to show the new file
                } catch (error) {
                    this.log.error("Error finalizing upload:", error);
                    this.appClient.notifier.error(`Finalization failed: ${error.message}`);
                } finally {
                    this.fileUpload.clear(); // Always clear the uploader state
                }
            }
        });

        this._attachEventListeners();
        await super.initialize();
    }


    async render() {
        // This method is called to update the UI. The main modal structure is built in initialize().
        if (!this.container) {
            // If the container doesn't exist yet, initialize will handle it.
            // This is a safeguard.
            return;
        }

        // Re-render the dynamic parts of the modal based on the current state
        this._renderToolbar();
        this._renderFileGrid();
        this._updateFooter();

        // Apply translations to any newly rendered text content
        this.appClient.tx.applyTranslations(this.container);

        return this.container;
    }

    show(options = {}) {
        this.options = options;
        this.scope = 'subscription';
        this.currentPath = '';
        this.selectedFiles.clear();

        return new Promise((resolve, reject) => {
            this._resolvePromise = resolve;
            this._rejectPromise = reject;
            this._loadFiles();
            this.modalInstance.show();
        });
    }

    hide() {
        this.modalInstance.hide();
    }

    _attachEventListeners() {
        this.container.querySelectorAll('.nav-link[data-scope]').forEach(link => {
            link.addEventListener('click', (e) => this._handleScopeChange(e));
        });
        this.container.querySelector('.confirm-selection-btn').addEventListener('click', () => this._handleConfirmSelection());
        this.container.addEventListener('hidden.bs.modal', () => this._handleCancel());

        const grid = this.container.querySelector('.media-grid-container');
        grid.addEventListener('dragover', this._boundHandleDragOver);
        grid.addEventListener('drop', this._boundHandleDrop);
    }

    async _loadFiles() {
        const gridContainer = this.container.querySelector('.media-grid-container');
        gridContainer.innerHTML = `<div class="spinner-border text-primary" role="status"></div>`;

        try {
            this.files = await this.appClient.rpcClient.call('MediaManagerRpc.listFiles', {
                scope: this.scope,
                path: this.currentPath
            });
            this._renderToolbar();
            this._renderFileGrid();
            this._updateFooter();
        } catch (error) {
            this.appClient.notifier.error("Could not load media files.");
            gridContainer.innerHTML = `<p class="text-danger">${error.message}</p>`;
        }
    }

    _renderToolbar() {
        const toolbar = this.container.querySelector('.media-toolbar');
        const isWritable = this.scope === 'subscription';
        toolbar.innerHTML = `
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <nav class="media-breadcrumbs"><ol class="breadcrumb mb-0"></ol></nav>
                <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" id="mm-upload-btn" ${!isWritable ? 'disabled' : ''}><i class="bi bi-upload"></i> Upload</button>
                    <button class="btn btn-outline-secondary" id="mm-import-btn" ${!isWritable ? 'disabled' : ''}><i class="bi bi-link-45deg"></i> Import URL</button>
                    <button class="btn btn-outline-secondary" id="mm-folder-btn" ${!isWritable ? 'disabled' : ''}><i class="bi bi-folder-plus"></i> New Folder</button>
                </div>
            </div>
        `;
        this._renderBreadcrumbs();
        toolbar.querySelector('#mm-upload-btn').addEventListener('click', () => this.fileUpload.openFile());
        toolbar.querySelector('#mm-import-btn').addEventListener('click', () => this._handleImportUrl());
        toolbar.querySelector('#mm-folder-btn').addEventListener('click', () => this._handleCreateFolder());
    }

    _renderBreadcrumbs() {
        const ol = this.container.querySelector('.media-breadcrumbs ol');
        ol.innerHTML = '';
        const parts = this.currentPath.split('/').filter(Boolean);

        const rootLi = document.createElement('li');
        rootLi.className = 'breadcrumb-item';
        rootLi.innerHTML = `<a href="#" data-path="">${this.scope === 'global' ? 'Media Library' : 'My Media'}</a>`;
        rootLi.querySelector('a').addEventListener('click', (e) => {
            e.preventDefault();
            this.currentPath = '';
            this._loadFiles();
        });
        ol.appendChild(rootLi);

        let current = '';
        parts.forEach((part, index) => {
            current += (current ? '/' : '') + part;
            const li = document.createElement('li');
            li.className = 'breadcrumb-item';
            if (index < parts.length - 1) {
                li.innerHTML = `<a href="#" data-path="${current}">${part}</a>`;
                li.querySelector('a').addEventListener('click', (e) => {
                    e.preventDefault();
                    this.currentPath = e.target.dataset.path;
                    this._loadFiles();
                });
            } else {
                li.textContent = part;
                li.classList.add('active');
            }
            ol.appendChild(li);
        });
    }

    _renderFileGrid() {
        const grid = this.container.querySelector('.media-grid-container');
        grid.innerHTML = '';
        this.files.forEach(item => {
            const itemEl = document.createElement('div');
            itemEl.className = 'media-item';
            itemEl.dataset.relativePath = item.relativePath;
            itemEl.dataset.type = item.type;

            let iconHtml = '';
            if (item.type === 'directory') {
                iconHtml = '<i class="bi bi-folder-fill item-icon"></i>';
            } else {
                // --- START FIX ---
                // The client is now responsible for constructing the full URL for display.
                const scopePrefix = this.scope === 'global' ? 'global' : this.appClient.getSharedData('subscriptionId');
                const fullUrl = `/media/${scopePrefix}/${item.relativePath}`;
                // --- END FIX ---

                if (/\.(jpg|jpeg|png|gif|webp)$/i.test(item.name)) {
                    iconHtml = `<img src="${fullUrl}" loading="lazy">`;
                } else if (/\.(mp4|webm|mov)$/i.test(item.name)) {
                    iconHtml = `<video src="${fullUrl}" muted playsinline></video>`;
                } else if (/\.(mp3|wav|ogg)$/i.test(item.name)) {
                    iconHtml = '<i class="bi bi-file-earmark-music item-icon"></i>';
                } else {
                    iconHtml = '<i class="bi bi-file-earmark item-icon"></i>';
                }
            }

            itemEl.innerHTML = `
                ${iconHtml}
                <span class="item-name">${item.name}</span>
                <input type="checkbox" class="form-check-input selection-checkbox" style="display: none;">
            `;

            itemEl.addEventListener('click', () => this._handleItemClick(item, itemEl));
            grid.appendChild(itemEl);
        });
    }



    _handleItemClick(item, element) {
        if (item.type === 'directory') {
            this.currentPath = item.relativePath;
            this._loadFiles();
        } else {
            this._toggleSelection(item, element);
        }
    }

    _toggleSelection(item, element) {
        const checkbox = element.querySelector('.selection-checkbox');
        if (this.selectedFiles.has(item.relativePath)) {
            this.selectedFiles.delete(item.relativePath);
            element.classList.remove('selected');
            checkbox.checked = false;
        } else {
            if (this.options.maxFiles === 1) {
                this.selectedFiles.clear();
                this.container.querySelectorAll('.media-item.selected').forEach(el => el.classList.remove('selected'));
                this.container.querySelectorAll('.selection-checkbox').forEach(cb => cb.checked = false);
            }
            if (this.selectedFiles.size < this.options.maxFiles) {
                this.selectedFiles.set(item.relativePath, item);
                element.classList.add('selected');
                checkbox.checked = true;
            }
        }
        this._updateFooter();
    }

    _updateFooter() {
        const info = this.container.querySelector('.selection-info');
        const btn = this.container.querySelector('.confirm-selection-btn');
        const count = this.selectedFiles.size;
        info.textContent = `${count} / ${this.options.maxFiles} selected`;
        btn.disabled = count === 0;
    }

    async _handleCreateFolder() {
        const folderName = await this.appClient.notifier.prompt("Enter new folder name:");
        if (folderName) {
            try {
                await this.appClient.rpcClient.call('MediaManagerRpc.createFolder', {
                    scope: this.scope,
                    currentPath: this.currentPath,
                    folderName
                });
                this._loadFiles();
            } catch (error) {
                this.appClient.notifier.error(`Could not create folder: ${error.message}`);
            }
        }
    }

    async _handleImportUrl() {
        const url = await this.appClient.notifier.prompt("Enter public URL to import:");
        if (url) {
            try {
                await this.appClient.rpcClient.call('MediaManagerRpc.importFromUrl', {
                    scope: this.scope,
                    currentPath: this.currentPath,
                    url
                });
                this._loadFiles();
            } catch (error) {
                this.appClient.notifier.error(`Could not import from URL: ${error.message}`);
            }
        }
    }

    _handleDrop(event) {
        event.preventDefault();
        if (this.scope !== 'subscription') return;
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            this.fileUpload.addFiles(files);
        }
    }

    _handleScopeChange(event) {
        event.preventDefault();
        this.container.querySelectorAll('.nav-link[data-scope]').forEach(link => link.classList.remove('active'));
        event.target.classList.add('active');
        this.scope = event.target.dataset.scope;
        this.currentPath = '';
        this.selectedFiles.clear();
        this._loadFiles();
    }

    _handleConfirmSelection() {
        if (this._resolvePromise) {
            const scopePrefix = this.scope === 'global' ? 'global' : this.appClient.getSharedData('subscriptionId');

            const selectedItems = Array.from(this.selectedFiles.values()).map(item => {
                const fullRelativePath = `${scopePrefix}/${item.relativePath}`;
                return { ...item, relativePath: fullRelativePath };
            });

            this._resolvePromise(selectedItems);
        }
        this.hide();
    }





    _handleCancel() {
        if (this._rejectPromise) {
            this._rejectPromise('cancelled');
        }
    }

    destroy() {
        this.modalInstance?.dispose();
        this.container?.remove();
        super.destroy();
    }
}